import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Result, Spin, message } from 'antd';
import { WarningFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { getBaseURL } from '../../services/api';
import useNewConfirmationSound from '../../hooks/useNewConfirmationSound';
import './PublicConvocationPage.css';

// Página pública acessada via link do email/whatsapp/telegram. Sem auth.
// Valida HMAC no backend. Aceita ?a=going|not_going|waiting pra ação
// direta (clicou no botão do email) — mas SEMPRE valida server-side.

// baseURL já inclui /api — chamadas usam paths sem prefixo /api.
const publicApi = axios.create({ baseURL: getBaseURL() });

// Cooldown de 5min pra trocar de opção depois de responder (evita spam
// e reflete no botão desabilitado — backend também bloqueia).
const COOLDOWN_MS = 5 * 60 * 1000;

const ACTION_META = {
  going:     { label: 'Vou jogar',          icon: '⚽', cls: 'going',
               modalTitle: 'Bora! Você tá dentro?',
               modalSub: 'Vamos avisar o organizador que pode contar com você.',
               confirmLabel: 'Confirmar presença',
               badge: 'Confirmado! Você tá dentro nessa.' },
  waiting:   { label: 'Fico na espera',     icon: '🪑', cls: 'waiting',
               modalTitle: 'Fica na espera?',
               modalSub: 'Se abrir vaga, avisamos você primeiro.',
               confirmLabel: 'Ficar na espera',
               badge: 'Na espera. Se abrir vaga, te chamamos.' },
  not_going: { label: 'Não vou dessa vez',  icon: '✋', cls: 'not_going',
               modalTitle: 'Não vai dar dessa vez?',
               modalSub: 'Sem estresse. Fica pra próxima!',
               confirmLabel: 'Confirmar ausência',
               badge: 'Beleza! Fica pra próxima.' },
};

// Posições táticas pré-definidas dentro da quadra (% left/top).
// Ordem = ordem de preenchimento; até 12 no campo, resto vai visualmente pra Espera.
const FIELD_SPOTS = [
  { left: '50%', top: '58%' }, // atacante central
  { left: '30%', top: '30%' }, { left: '70%', top: '30%' },
  { left: '22%', top: '48%' }, { left: '78%', top: '48%' },
  { left: '50%', top: '38%' },
  { left: '35%', top: '68%' }, { left: '65%', top: '68%' },
  { left: '45%', top: '78%' }, { left: '55%', top: '78%' },
  { left: '40%', top: '22%' }, { left: '60%', top: '22%' },
];
// Espera: posições no topo
const WAITING_SPOTS = [
  { left: '50%', top: '9%' }, { left: '32%', top: '9%' }, { left: '68%', top: '9%' },
];
// Fora: posições no bottom
const OUT_SPOTS = [
  { left: '50%', top: '93%' }, { left: '32%', top: '93%' }, { left: '68%', top: '93%' },
];
// Cores de pele pra dar variação visual (deterministic pelo nome).
const SKIN_TONES = ['#f2c99a', '#e6b48c', '#c69371', '#a56a45', '#8b5a3c', '#623a25'];
function skinFor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return SKIN_TONES[h % SKIN_TONES.length];
}

function MiniPlayer({ shirt, shirtText = '#422006', skin, number, small = false }) {
  return (
    <svg className="pcp-mini-player" viewBox="0 0 40 60" aria-hidden="true"
         style={small ? { width: 38, height: 50 } : undefined}>
      <ellipse className="pcp-shadow-el" cx="20" cy="55" rx="10" ry="1.8" fill="rgba(0,0,0,0.55)"/>
      <g className="pcp-leg pcp-leg-left"><rect x="14.5" y="40" width="4" height="14" rx="1.8" fill="#0f172a"/></g>
      <g className="pcp-leg pcp-leg-right"><rect x="21.5" y="40" width="4" height="14" rx="1.8" fill="#0f172a"/></g>
      <g className="pcp-arm pcp-arm-left"><rect x="6.5" y="22" width="3.5" height="14" rx="1.6" fill={skin}/></g>
      <g className="pcp-arm pcp-arm-right"><rect x="30" y="22" width="3.5" height="14" rx="1.6" fill={skin}/></g>
      <path d="M 10 22 L 10 42 Q 10 45 13 45 L 27 45 Q 30 45 30 42 L 30 22 Z" fill={shirt}/>
      <path d="M 16 22 L 20 26 L 24 22 Z" fill="rgba(0,0,0,0.25)"/>
      {number != null && (
        <text x="20" y="38" textAnchor="middle" fill={shirtText} fontSize="9" fontWeight="900" fontFamily="system-ui">{number}</text>
      )}
      <g className="pcp-head-grp">
        <circle cx="20" cy="14" r="7" fill={skin}/>
        <circle cx="17.5" cy="13.5" r="0.8" fill="#0f172a"/>
        <circle cx="22.5" cy="13.5" r="0.8" fill="#0f172a"/>
        <path d="M 17.5 16 Q 20 17.5 22.5 16" stroke="#0f172a" strokeWidth="0.8" fill="none" strokeLinecap="round"/>
      </g>
    </svg>
  );
}

export default function PublicConvocationPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const initialAction = searchParams.get('a');

  const [state, setState] = useState('loading');
  const [data, setData] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [autoSubmitted, setAutoSubmitted] = useState(false);
  const playerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await publicApi.get(`/c/${token}`);
      setData(r.data);
      setState(r.data.convocation_status === 'cancelled' ? 'cancelled' : 'ok');
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'expired') setState('expired');
      else if (code === 'invalid') setState('invalid');
      else setState('error');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Polling leve pra mostrar novos confirmados sem precisar dar refresh.
  // 20s balanceia responsividade + carga. Se estado nao for 'ok', pausa.
  useEffect(() => {
    if (state !== 'ok') return undefined;
    const id = setInterval(() => { load(); }, 20000);
    return () => clearInterval(id);
  }, [state, load]);

  // Som ao detectar novos confirmados (going) na lista de others.
  const goingOthers = useMemo(
    () => (data?.others || []).filter(o => o.status === 'going').map((o, i) => ({ id: `${o.name}-${i}` })),
    [data]
  );
  const sound = useNewConfirmationSound(goingOthers, `pub-${token}`);

  // Distribui confirmados pelas posições táticas / espera / fora.
  const positioned = useMemo(() => {
    const others = data?.others || [];
    const going = others.filter(o => o.status === 'going');
    const waiting = others.filter(o => o.status === 'waiting');
    const notGoing = others.filter(o => o.status === 'not_going');
    const items = [];
    going.slice(0, FIELD_SPOTS.length).forEach((p, i) => {
      items.push({ ...p, kind: 'going', ...FIELD_SPOTS[i], shirt: '#22c55e', skin: skinFor(p.name) });
    });
    waiting.slice(0, WAITING_SPOTS.length).forEach((p, i) => {
      items.push({ ...p, kind: 'waiting', ...WAITING_SPOTS[i], shirt: '#94a3b8', skin: skinFor(p.name) });
    });
    notGoing.slice(0, OUT_SPOTS.length).forEach((p, i) => {
      items.push({ ...p, kind: 'out', ...OUT_SPOTS[i], shirt: '#475569', skin: skinFor(p.name) });
    });
    return items;
  }, [data]);

  // Escolhe posição livre pra "eu" (evita colisão com os outros).
  const mySpot = useCallback((action) => {
    const others = data?.others || [];
    if (action === 'going') {
      const usedCount = others.filter(o => o.status === 'going').length;
      return FIELD_SPOTS[usedCount % FIELD_SPOTS.length];
    }
    if (action === 'waiting') {
      const usedCount = others.filter(o => o.status === 'waiting').length;
      return WAITING_SPOTS[usedCount % WAITING_SPOTS.length];
    }
    if (action === 'not_going') {
      const usedCount = others.filter(o => o.status === 'not_going').length;
      return OUT_SPOTS[usedCount % OUT_SPOTS.length];
    }
    return null;
  }, [data]);

  const runAnimation = useCallback((action) => {
    const el = playerRef.current;
    if (!el) return;
    // Reset
    el.style.transition = 'none';
    el.classList.remove('visible', 'walking', 'waving', 'landed');
    el.style.left = '50%';
    el.style.top = '-18%';
    el.style.transform = 'translate(-50%, -50%) scale(0.7)';
    el.style.opacity = '';
    // Force reflow
    // eslint-disable-next-line no-unused-expressions
    el.getBoundingClientRect();

    setTimeout(() => {
      el.classList.add('visible', 'walking');
      el.style.transition = 'top 1.1s cubic-bezier(0.34, 1.4, 0.64, 1), left 1.1s cubic-bezier(0.34, 1.4, 0.64, 1), transform 1.1s cubic-bezier(0.34, 1.4, 0.64, 1), opacity 0.4s';

      if (action === 'going' || action === 'waiting') {
        const spot = mySpot(action);
        el.style.left = spot.left;
        el.style.top = spot.top;
        el.style.transform = `translate(-50%, -50%) scale(${action === 'waiting' ? 0.85 : 1})`;
        setTimeout(() => {
          el.classList.remove('walking');
          el.classList.add('landed');
        }, 1100);
      } else if (action === 'not_going') {
        // Entra, acena e vai pra zona Fora (apagadinho)
        el.style.left = '50%';
        el.style.top = '45%';
        el.style.transform = 'translate(-50%, -50%) scale(1)';
        setTimeout(() => { el.classList.remove('walking'); el.classList.add('waving'); }, 750);
        setTimeout(() => {
          el.classList.remove('waving');
          el.classList.add('walking');
          const spot = mySpot('not_going');
          el.style.transition = 'left 0.9s cubic-bezier(0.34, 1.4, 0.64, 1), top 0.9s cubic-bezier(0.34, 1.4, 0.64, 1), transform 0.9s cubic-bezier(0.34, 1.4, 0.64, 1), opacity 0.5s, filter 0.5s';
          el.style.left = spot.left;
          el.style.top = spot.top;
          el.style.transform = 'translate(-50%, -50%) scale(0.7)';
          el.style.opacity = '0.5';
          el.style.filter = 'saturate(0.4)';
        }, 1600);
        setTimeout(() => {
          el.classList.remove('walking');
          el.classList.add('landed', 'is-out');
        }, 2500);
      }
    }, 40);
  }, [mySpot]);

  const startAction = useCallback((action) => {
    if (pendingAction) return;
    setPendingAction(action);
    runAnimation(action);
    const delay = action === 'not_going' ? 2200 : 1200;
    setTimeout(() => setPendingAction(prev => prev === action ? action : prev), 0);
    // O modal renderiza via state.pendingAction após o timer:
    setTimeout(() => {
      // marker pra abrir modal (usa outro state)
      setModalAction(action);
    }, delay);
  }, [pendingAction, runAnimation]);

  const [modalAction, setModalAction] = useState(null);

  const cancelModal = useCallback(() => {
    setModalAction(null);
    setPendingAction(null);
    // Reseta jogador pra posição inicial (fora do campo).
    const el = playerRef.current;
    if (el) {
      el.style.transition = 'opacity 0.3s';
      el.classList.remove('visible', 'walking', 'waving', 'landed');
    }
  }, []);

  const respond = useCallback(async () => {
    if (!modalAction) return;
    const action = modalAction;
    try {
      const r = await publicApi.post(`/c/${token}/respond`, { action });
      setData(prev => prev ? {
        ...prev,
        status: r.data.status,
        responded_at: new Date().toISOString(),
      } : prev);
      setModalAction(null);
      setPendingAction(null);
      message.success('Resposta salva!');
      // Recarrega pra atualizar lista de outros confirmados.
      load();
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'cancelled') { setState('cancelled'); return; }
      if (code === 'cooldown') {
        setModalAction(null);
        setPendingAction(null);
      }
      message.error(err.response?.data?.message || 'Erro ao salvar');
    }
  }, [modalAction, token, load]);

  // Tick pra atualizar contador de cooldown a cada segundo.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const cooldownLeftMs = useMemo(() => {
    if (!data?.responded_at) return 0;
    const elapsed = now - new Date(data.responded_at).getTime();
    return Math.max(0, COOLDOWN_MS - elapsed);
  }, [data, now]);
  const cooldownLabel = useMemo(() => {
    if (cooldownLeftMs <= 0) return '';
    const s = Math.ceil(cooldownLeftMs / 1000);
    const m = Math.floor(s / 60);
    const ss = String(s % 60).padStart(2, '0');
    return `${m}:${ss}`;
  }, [cooldownLeftMs]);

  // Auto-submit se ?a=... veio na URL (já dispara a animação e o modal).
  useEffect(() => {
    if (state === 'ok' && initialAction && !autoSubmitted && data && data.status === 'pending') {
      if (['going', 'waiting', 'not_going'].includes(initialAction)) {
        setAutoSubmitted(true);
        startAction(initialAction);
      }
    }
  }, [state, initialAction, autoSubmitted, data, startAction]);

  // Se o jogador ja respondeu (refresh/volta depois), posiciona o boneco
  // direto no lugar sem animar — senao fica invisivel (CSS default opacity 0).
  useEffect(() => {
    if (state !== 'ok' || !data) return;
    if (pendingAction) return; // animacao em curso — deixa ela cuidar
    const el = playerRef.current;
    if (!el) return;
    const s = data.status;
    if (s === 'going' || s === 'waiting' || s === 'not_going') {
      let spot, scale, extraCls = '', opacity = '', filter = '';
      const others = data.others || [];
      if (s === 'going') {
        spot = FIELD_SPOTS[others.filter(o => o.status === 'going').length % FIELD_SPOTS.length];
        scale = 1;
      } else if (s === 'waiting') {
        spot = WAITING_SPOTS[others.filter(o => o.status === 'waiting').length % WAITING_SPOTS.length];
        scale = 0.85;
      } else {
        spot = OUT_SPOTS[others.filter(o => o.status === 'not_going').length % OUT_SPOTS.length];
        scale = 0.7;
        extraCls = 'is-out';
        opacity = '0.5';
        filter = 'saturate(0.4)';
      }
      el.style.transition = 'none';
      el.style.left = spot.left;
      el.style.top = spot.top;
      el.style.transform = `translate(-50%, -50%) scale(${scale})`;
      el.style.opacity = opacity;
      el.style.filter = filter;
      el.classList.remove('walking', 'waving', 'is-out');
      el.classList.add('visible', 'landed');
      if (extraCls) el.classList.add(extraCls);
    }
  }, [state, data, pendingAction]);

  if (state === 'loading') {
    return <div style={{ padding: 60, textAlign: 'center' }}><Spin size="large" /></div>;
  }
  if (state === 'expired') {
    return <Result status="warning" icon={<WarningFilled style={{ color: '#faad14' }} />}
      title="Link expirado" subTitle="Essa convocação já passou do prazo. Fale com o organizador do time." />;
  }
  if (state === 'invalid') {
    return <Result status="error" title="Link inválido" subTitle="Confira o link do email ou peça um novo pro organizador." />;
  }
  if (state === 'error') {
    return <Result status="500" title="Ops" subTitle="Deu ruim aqui. Tenta de novo em alguns minutos." />;
  }

  const c = data;
  const firstName = (c.player_name || '').split(' ')[0] || 'boleiro';
  const currentStatus = c.status;
  const isLocked = currentStatus !== 'pending';
  const successMeta = isLocked ? ACTION_META[currentStatus] : null;

  return (
    <div className="pcp-root">
      <div className="pcp-card">
        <div className="pcp-brand">
          <div className="logo">
            {c.team_logo ? <img src={c.team_logo} alt="" /> : '⚽'}
          </div>
          <div>
            <div className="label">Convocação</div>
            <div className="team">{c.team_name}</div>
          </div>
        </div>

        {state === 'cancelled' ? (
          <Result status="warning" icon={<WarningFilled style={{ color: '#ef4444' }} />}
            title="Convocação cancelada"
            subTitle="O organizador cancelou. Aguarde novas instruções."
            style={{ padding: 0 }} />
        ) : (
          <>
            <div className="pcp-meta">
              <div className="pcp-meta-date">{dayjs(c.match_date).format('dddd · DD/MM')}</div>
              <div className="pcp-meta-time">{String(c.match_time).slice(0, 5)}</div>
              <div className="pcp-meta-place">
                <span className="dot"></span>
                {c.quadra || 'Local a definir'}{c.city ? ` · ${c.city}` : ''}
              </div>
              {c.notes && <div className="pcp-notes">{c.notes}</div>}
            </div>

            <div className="pcp-stadium-wrap">
              <svg className="pcp-stadium" viewBox="0 0 300 400" preserveAspectRatio="none" aria-hidden="true">
                <defs>
                  <pattern id="pcp-stripes" x="0" y="0" width="300" height="50" patternUnits="userSpaceOnUse">
                    <rect width="300" height="50" fill="#0a4a2e"/>
                    <rect y="25" width="300" height="25" fill="#0d5c39"/>
                  </pattern>
                </defs>
                <rect width="300" height="400" fill="url(#pcp-stripes)"/>
                <rect x="14" y="14" width="272" height="372" fill="none" stroke="rgba(244,244,245,0.85)" strokeWidth="2" rx="2"/>
                <line x1="14" y1="200" x2="286" y2="200" stroke="rgba(244,244,245,0.85)" strokeWidth="2"/>
                <circle cx="150" cy="200" r="42" fill="none" stroke="rgba(244,244,245,0.85)" strokeWidth="2"/>
                <circle cx="150" cy="200" r="3" fill="rgba(244,244,245,0.85)"/>
                <rect x="70" y="14" width="160" height="60" fill="none" stroke="rgba(244,244,245,0.85)" strokeWidth="2"/>
                <rect x="105" y="14" width="90" height="24" fill="none" stroke="rgba(244,244,245,0.85)" strokeWidth="2"/>
                <circle cx="150" cy="60" r="2.5" fill="rgba(244,244,245,0.85)"/>
                <path d="M 120 74 A 34 34 0 0 0 180 74" fill="none" stroke="rgba(244,244,245,0.85)" strokeWidth="2"/>
                <rect x="70" y="326" width="160" height="60" fill="none" stroke="rgba(244,244,245,0.85)" strokeWidth="2"/>
                <rect x="105" y="362" width="90" height="24" fill="none" stroke="rgba(244,244,245,0.85)" strokeWidth="2"/>
                <circle cx="150" cy="340" r="2.5" fill="rgba(244,244,245,0.85)"/>
                <path d="M 120 326 A 34 34 0 0 1 180 326" fill="none" stroke="rgba(244,244,245,0.85)" strokeWidth="2"/>
              </svg>

              <div className="pcp-zone-label pcp-zone-espera">Espera</div>
              {positioned.some(p => p.kind === 'out') || c.status === 'not_going' ? (
                <div className="pcp-zone-label pcp-zone-fora">Fora</div>
              ) : null}

              <div className="pcp-confirmed-layer">
                {positioned.map((p, i) => (
                  <div key={`${p.kind}-${i}-${p.name}`}
                       className={`pcp-confirmed-player is-${p.kind}`}
                       style={{ left: p.left, top: p.top, animationDelay: `${i * 60}ms` }}>
                    <MiniPlayer shirt={p.shirt} skin={p.skin} small />
                    <div className="pcp-player-name">{p.name}</div>
                  </div>
                ))}
              </div>

              <div ref={playerRef} className="pcp-player is-me">
                <MiniPlayer shirt="#eab308" shirtText="#422006" skin="#fbbf24" number="10" />
                <div className="pcp-player-name">Você</div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end',
                          fontSize: 11, color: 'var(--text-dim)', margin: '-10px 4px 12px' }}>
              <button
                onClick={sound.toggle}
                style={{
                  background: 'transparent', border: '1px solid rgba(148,163,184,0.3)',
                  color: sound.enabled ? '#facc15' : 'var(--text-dim)',
                  padding: '3px 10px', borderRadius: 999, fontSize: 11, cursor: 'pointer',
                  fontWeight: 600,
                }}
                title={sound.enabled ? 'Som ligado — desligar' : 'Tocar som quando alguém confirmar'}>
                {sound.enabled ? '🔔 Som' : '🔕 Silêncio'}
              </button>
            </div>

            {isLocked && successMeta && (
              <div className={`pcp-success ${successMeta.cls}`}>
                <span>{successMeta.icon}</span>
                <span>{successMeta.badge}</span>
                {c.responded_at && (
                  <span className="responded-at">· {dayjs(c.responded_at).format('DD/MM HH:mm')}</span>
                )}
              </div>
            )}

            <div className="pcp-prompt">
              <div className="greet">E aí, <span className="accent">{firstName}</span>!</div>
              <div className="q">
                {isLocked ? 'Trocou de ideia? É só clicar de novo.' : 'Bora dar as caras nessa pelada?'}
              </div>
            </div>

            <div className={`pcp-actions ${isLocked ? 'locked' : ''}`}>
              {['going', 'waiting', 'not_going'].map((action) => {
                const meta = ACTION_META[action];
                const isCurrent = currentStatus === action;
                const inCooldown = isLocked && !isCurrent && cooldownLeftMs > 0;
                return (
                  <button key={action}
                          className={`pcp-btn-${action.replace('_', '-')} ${isCurrent ? 'active-choice' : ''}`}
                          onClick={() => startAction(action)}
                          disabled={!!pendingAction || inCooldown}
                          title={inCooldown ? `Aguarde ${cooldownLabel} pra trocar` : ''}>
                    <span className="ico">{meta.icon}</span>
                    <span>{meta.label}</span>
                    <span className="arr">→</span>
                  </button>
                );
              })}
            </div>

            {isLocked && cooldownLeftMs > 0 && (
              <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-dim)', marginTop: -8, marginBottom: 14 }}>
                ⏱ Pra trocar de opção, espera <strong>{cooldownLabel}</strong>
              </div>
            )}

            <div className="pcp-foot">
              <span className="accent">Futezap Comunidade</span> · Feito no Brasil 🇧🇷
            </div>
          </>
        )}
      </div>

      {modalAction && (
        <div className="pcp-backdrop open" onClick={(e) => e.target.classList.contains('pcp-backdrop') && cancelModal()}>
          <div className="pcp-modal-card">
            <div className={`pcp-modal-icon ${ACTION_META[modalAction].cls}`}>{ACTION_META[modalAction].icon}</div>
            <div className="pcp-modal-title">{ACTION_META[modalAction].modalTitle}</div>
            <div className="pcp-modal-sub">{ACTION_META[modalAction].modalSub}</div>
            <div className="pcp-modal-actions">
              <button className="cancel" onClick={cancelModal}>Voltar</button>
              <button className={`confirm ${ACTION_META[modalAction].cls}`} onClick={respond}>
                {ACTION_META[modalAction].confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
